import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { useAdminSupportThreads, SupportThread, useUpdateSupportThread } from "@/hooks/useSupportThreads";
import { formatDistanceToNow } from "date-fns";
import { MessageCircle, Clock, CheckCircle, AlertCircle, User, Filter, RefreshCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { AdminThreadDetail } from "./AdminThreadDetail";
import { useQueryClient } from "@tanstack/react-query";
import { getStorageUrl } from "@/lib/storage";

const STATUS_CONFIG = {
  open: {
    label: "Open",
    className: "bg-blue-100 text-blue-800",
    icon: MessageCircle,
  },
  awaiting_user: {
    label: "Awaiting User",
    className: "bg-amber-100 text-amber-800",
    icon: AlertCircle,
  },
  resolved: {
    label: "Resolved",
    className: "bg-green-100 text-green-800",
    icon: CheckCircle,
  },
};

const CATEGORIES = [
  { value: "all", label: "All Categories" },
  { value: "account", label: "Account" },
  { value: "courses", label: "Courses" },
  { value: "mentorship", label: "Mentorship" },
  { value: "technical", label: "Technical" },
  { value: "other", label: "Other" },
];

interface AskNedraInboxProps {
  initialThreadId?: string | null;
}

export function AskNedraInbox({ initialThreadId }: AskNedraInboxProps) {
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(initialThreadId || null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [assignedToMe, setAssignedToMe] = useState(false);
  const queryClient = useQueryClient();

  // Handle deep-linking when initialThreadId changes
  useEffect(() => {
    if (initialThreadId) {
      setSelectedThreadId(initialThreadId);
    }
  }, [initialThreadId]);

  const { data: threads, isLoading, refetch } = useAdminSupportThreads({
    status: statusFilter,
    category: categoryFilter,
    assignedToMe,
  });

  if (selectedThreadId) {
    return (
      <AdminThreadDetail
        threadId={selectedThreadId}
        onBack={() => setSelectedThreadId(null)}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">💬</span>
            Ask Nedra - Support Inbox
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCcw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Label htmlFor="status-filter" className="text-sm">Status:</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger id="status-filter" className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="awaiting_user">Awaiting User</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="category-filter" className="text-sm">Category:</Label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger id="category-filter" className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="assigned-to-me"
              checked={assignedToMe}
              onCheckedChange={(checked) => setAssignedToMe(checked === true)}
            />
            <Label htmlFor="assigned-to-me" className="text-sm cursor-pointer">
              Assigned to me
            </Label>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-700">
              {threads?.filter((t) => t.status === "open").length || 0}
            </div>
            <div className="text-sm text-blue-600">Open</div>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-amber-700">
              {threads?.filter((t) => t.status === "awaiting_user").length || 0}
            </div>
            <div className="text-sm text-amber-600">Awaiting User</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-700">
              {threads?.filter((t) => t.status === "resolved").length || 0}
            </div>
            <div className="text-sm text-green-600">Resolved</div>
          </div>
        </div>

        {/* Threads Table */}
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : !threads?.length ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No support threads found</p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Thread</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead className="w-[120px]">Category</TableHead>
                  <TableHead className="w-[130px]">Status</TableHead>
                  <TableHead className="w-[130px]">Assigned</TableHead>
                  <TableHead className="w-[130px]">Last Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {threads.map((thread) => (
                  <ThreadRow
                    key={thread.id}
                    thread={thread}
                    onClick={() => setSelectedThreadId(thread.id)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ThreadRow({
  thread,
  onClick,
}: {
  thread: SupportThread;
  onClick: () => void;
}) {
  const status = STATUS_CONFIG[thread.status] || STATUS_CONFIG.open;
  const StatusIcon = status.icon;

  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/50"
      onClick={onClick}
    >
      <TableCell className="font-mono text-xs">
        {thread.id.substring(0, 8)}...
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8">
            <AvatarImage src={getStorageUrl("avatars", thread.user_avatar) || undefined} />
            <AvatarFallback className="text-xs">
              {thread.user_name?.charAt(0) || "?"}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium text-sm">{thread.user_name}</div>
            <div className="text-xs text-muted-foreground">{thread.user_email}</div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        {thread.category ? (
          <span className="capitalize text-sm">{thread.category}</span>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        )}
      </TableCell>
      <TableCell>
        <Badge className={cn("text-xs", status.className)}>
          <StatusIcon className="h-3 w-3 mr-1" />
          {status.label}
        </Badge>
      </TableCell>
      <TableCell>
        {thread.assigned_admin_name ? (
          <span className="text-sm">{thread.assigned_admin_name}</span>
        ) : (
          <span className="text-muted-foreground text-sm">Unassigned</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          {formatDistanceToNow(new Date(thread.last_message_at), { addSuffix: true })}
        </div>
      </TableCell>
    </TableRow>
  );
}
